import React, { useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Paper,
  Typography,
} from '@mui/material';
import SecurityIcon from '@mui/icons-material/Security';
import { useTranslation } from 'react-i18next';
import { useUser } from '../WebSocketContext';
import { useComponentWebSocket } from './GlobalSettingUtils';

const LOGIN_EVENT_PATTERN = /login_(success|failed|blocked|lockout|error)/;
const LOGIN_ACTIVITY_REQUEST_ID = 'global_settings_login_activity';
const LOGIN_ACTIVITY_PAGE_SIZE = 25;

const getLoginEventChipColor = (eventType) => {
  switch (eventType) {
    case 'login_success':
      return 'success';
    case 'login_blocked':
      return 'warning';
    case 'login_lockout':
    case 'login_error':
    case 'login_failed':
      return 'error';
    default:
      return 'default';
  }
};

const getLoginEventTextKey = (eventType) => {
  switch (eventType) {
    case 'login_success':
      return 'account_settings.login_activity_event_success';
    case 'login_blocked':
      return 'account_settings.login_activity_event_blocked';
    case 'login_lockout':
      return 'account_settings.login_activity_event_lockout';
    case 'login_error':
      return 'account_settings.login_activity_event_error';
    default:
      return 'account_settings.login_activity_event_failed';
  }
};

const formatLoginActivityTimestamp = (timestamp) => {
  if (!timestamp) {
    return '-';
  }

  let dateValue = timestamp;
  const legacyUtcPattern = /^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/;

  if (legacyUtcPattern.test(timestamp)) {
    dateValue = `${timestamp.replace(' ', 'T')}Z`;
  }

  const parsedDate = new Date(dateValue);
  if (Number.isNaN(parsedDate.getTime())) {
    return timestamp;
  }

  return parsedDate.toLocaleString();
};

const LoginActivitySettings = () => {
  const { t } = useTranslation();
  const { isAdmin } = useUser();
  const [loginActivity, setLoginActivity] = useState([]);
  const [loginActivityLoading, setLoginActivityLoading] = useState(false);
  const [loginActivityError, setLoginActivityError] = useState('');
  const [loginActivityLastUpdated, setLoginActivityLastUpdated] = useState('');
  const [loginActivityCursor, setLoginActivityCursor] = useState(null);
  const [loginActivityNextCursor, setLoginActivityNextCursor] = useState(null);
  const [loginActivityPreviousCursors, setLoginActivityPreviousCursors] = useState([]);
  const [loginActivityHasMore, setLoginActivityHasMore] = useState(false);
  const loginActivityPendingRequestRef = useRef({
    targetCursor: null,
    nextStack: [],
  });

  const commandHandlers = {
    get_login_activity_response: (message) => {
      const responseData = message.data || {};
      const requestId = responseData.request_id;
      const pendingRequest = loginActivityPendingRequestRef.current;

      if (requestId && requestId !== LOGIN_ACTIVITY_REQUEST_ID) {
        return;
      }

      setLoginActivityLoading(false);

      if (!responseData.success) {
        setLoginActivity([]);
        setLoginActivityError(responseData.message || t('account_settings.login_activity_load_failed'));
        setLoginActivityNextCursor(null);
        setLoginActivityHasMore(false);
        return;
      }

      const rawEvents = Array.isArray(responseData.events) ? responseData.events : [];
      const parsedEvents = rawEvents
        .map((event, index) => {
          const eventType = event?.event_type || event?.eventType || '';
          if (!LOGIN_EVENT_PATTERN.test(eventType || '')) {
            return null;
          }
          return {
            id: event?.id || index,
            timestamp: event?.timestamp || '',
            level: event?.level || 'INFO',
            message: event?.message || '',
            eventType,
            username: event?.username || '',
            ip: event?.ip || '',
            reason: event?.reason || '',
          };
        })
        .filter(Boolean);

      setLoginActivity(parsedEvents);
      setLoginActivityError('');
      setLoginActivityLastUpdated(new Date().toISOString());
      setLoginActivityCursor(
        responseData.cursor !== undefined && responseData.cursor !== null
          ? responseData.cursor
          : pendingRequest.targetCursor
      );
      setLoginActivityPreviousCursors(
        Array.isArray(pendingRequest.nextStack) ? pendingRequest.nextStack : []
      );
      setLoginActivityNextCursor(responseData.next_cursor ?? null);
      setLoginActivityHasMore(Boolean(responseData.has_more));
    },
  };

  const { sendCommand } = useComponentWebSocket(commandHandlers);

  useEffect(() => {
    if (!isAdmin) {
      setLoginActivity([]);
      setLoginActivityLoading(false);
      setLoginActivityError('');
      setLoginActivityLastUpdated('');
      setLoginActivityCursor(null);
      setLoginActivityNextCursor(null);
      setLoginActivityPreviousCursors([]);
      setLoginActivityHasMore(false);
      loginActivityPendingRequestRef.current = {
        targetCursor: null,
        nextStack: [],
      };
      return;
    }

    setLoginActivityLoading(true);
    setLoginActivityError('');
    loginActivityPendingRequestRef.current = {
      targetCursor: null,
      nextStack: [],
    };
    sendCommand('get_login_activity', {
      cursor: null,
      limit: LOGIN_ACTIVITY_PAGE_SIZE,
      request_id: LOGIN_ACTIVITY_REQUEST_ID,
    });
  }, [isAdmin, sendCommand]);

  const handleRefreshLoginActivity = () => {
    if (!isAdmin) {
      return;
    }
    setLoginActivityLoading(true);
    setLoginActivityError('');
    loginActivityPendingRequestRef.current = {
      targetCursor: loginActivityCursor,
      nextStack: [...loginActivityPreviousCursors],
    };
    sendCommand('get_login_activity', {
      cursor: loginActivityCursor,
      limit: LOGIN_ACTIVITY_PAGE_SIZE,
      request_id: LOGIN_ACTIVITY_REQUEST_ID,
    });
  };

  const handleLoginActivityPreviousPage = () => {
    if (!isAdmin || loginActivityLoading || loginActivityPreviousCursors.length === 0) {
      return;
    }
    const targetCursor = loginActivityPreviousCursors[loginActivityPreviousCursors.length - 1] ?? null;
    const nextStack = loginActivityPreviousCursors.slice(0, -1);
    setLoginActivityLoading(true);
    setLoginActivityError('');
    loginActivityPendingRequestRef.current = {
      targetCursor,
      nextStack,
    };
    sendCommand('get_login_activity', {
      cursor: targetCursor,
      limit: LOGIN_ACTIVITY_PAGE_SIZE,
      request_id: LOGIN_ACTIVITY_REQUEST_ID,
    });
  };

  const handleLoginActivityNextPage = () => {
    if (!isAdmin || loginActivityLoading || !loginActivityHasMore || !loginActivityNextCursor) {
      return;
    }
    const targetCursor = loginActivityNextCursor;
    const nextStack = [...loginActivityPreviousCursors, loginActivityCursor ?? null];
    setLoginActivityLoading(true);
    setLoginActivityError('');
    loginActivityPendingRequestRef.current = {
      targetCursor,
      nextStack,
    };
    sendCommand('get_login_activity', {
      cursor: targetCursor,
      limit: LOGIN_ACTIVITY_PAGE_SIZE,
      request_id: LOGIN_ACTIVITY_REQUEST_ID,
    });
  };

  const loginActivityCurrentPage = loginActivityPreviousCursors.length + 1;

  if (!isAdmin) {
    return (
      <Alert severity="warning">
        {t('account_management.admin_only')}
      </Alert>
    );
  }

  return (
    <Box sx={{ p: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, mb: 2, flexWrap: 'wrap' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <SecurityIcon sx={{ color: '#5757b0' }} />
            <Typography variant="h6" sx={{ fontWeight: 600, color: '#333' }}>
              {t('account_settings.login_activity_title')}
            </Typography>
          </Box>
          <Button
            variant="outlined"
            size="small"
            onClick={handleRefreshLoginActivity}
            disabled={loginActivityLoading}
            sx={{ textTransform: 'none' }}
          >
            {loginActivityLoading
              ? t('account_settings.login_activity_refreshing')
              : t('account_settings.login_activity_refresh')}
          </Button>
        </Box>

        <Typography variant="body2" sx={{ color: '#666', mb: 2 }}>
          {t('account_settings.login_activity_subtitle')}
        </Typography>

        {loginActivityLastUpdated && (
          <Typography variant="caption" sx={{ color: '#777', display: 'block', mb: 2 }}>
            {t('account_settings.login_activity_last_updated', { time: new Date(loginActivityLastUpdated).toLocaleString() })}
          </Typography>
        )}

        {loginActivityError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {loginActivityError}
          </Alert>
        )}

        {!loginActivityLoading && !loginActivityError && loginActivity.length === 0 && (
          <Alert severity="info" sx={{ mb: 2 }}>
            {t('account_settings.login_activity_empty')}
          </Alert>
        )}

        <Box
          sx={{
            border: '1px solid #e6e6f2',
            borderRadius: 2,
            maxHeight: 680,
            overflowY: 'auto',
            backgroundColor: 'white',
          }}
        >
          {loginActivity.map((event, index) => (
            <Box
              key={`${event.id}-${event.timestamp}-${event.username}-${index}`}
              sx={{
                px: 1,
                py: 1,
                display: 'flex',
                justifyContent: 'space-between',
                gap: 1,
                borderBottom: index === loginActivity.length - 1 ? 'none' : '1px solid #f1f1f8',
              }}
            >
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="body2" sx={{ fontWeight: 600, color: '#333' }}>
                  {t('account_settings.login_activity_user_label', {
                    username: event.username || t('account_settings.login_activity_unknown_user'),
                  })}
                </Typography>
                <Typography variant="caption" sx={{ color: '#666', display: 'block', mt: 0.5 }}>
                  {t('account_settings.login_activity_ip_label', {
                    ip: event.ip || t('account_settings.login_activity_unknown_ip'),
                  })}
                </Typography>
                {event.reason && (
                  <Typography variant="caption" sx={{ color: '#666', display: 'block' }}>
                    {t('account_settings.login_activity_reason_label', { reason: event.reason })}
                  </Typography>
                )}
              </Box>
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 0.75, flexShrink: 0 }}>
                <Chip
                  label={t(getLoginEventTextKey(event.eventType))}
                  color={getLoginEventChipColor(event.eventType)}
                  size="small"
                  sx={{ fontWeight: 600 }}
                />
                <Typography variant="caption" sx={{ color: '#666' }}>
                  {formatLoginActivityTimestamp(event.timestamp)}
                </Typography>
              </Box>
            </Box>
          ))}
        </Box>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 2,
            mt: 2,
            flexWrap: 'wrap',
          }}
        >
          <Box sx={{ display: 'flex', flexDirection: 'column' }}>
            <Typography variant="caption" sx={{ color: '#666' }}>
              {t('account_settings.login_activity_page_info', {
                page: loginActivityCurrentPage,
              })}
            </Typography>
            <Typography variant="caption" sx={{ color: '#666' }}>
              {t('account_settings.login_activity_results_info', {
                count: loginActivity.length,
              })}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant="outlined"
              size="small"
              onClick={handleLoginActivityPreviousPage}
              disabled={loginActivityLoading || loginActivityPreviousCursors.length === 0}
              sx={{ textTransform: 'none' }}
            >
              {t('account_settings.login_activity_previous')}
            </Button>
            <Button
              variant="outlined"
              size="small"
              onClick={handleLoginActivityNextPage}
              disabled={loginActivityLoading || !loginActivityHasMore}
              sx={{ textTransform: 'none' }}
            >
              {t('account_settings.login_activity_next')}
            </Button>
          </Box>
        </Box>
    </Box>
  );
};

export default LoginActivitySettings;
